import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Box, CircularProgress } from '@mui/material';
import {
  GET_COMMUNITY_USERS,
  GET_COMMUNITY_USERS_MAP,
  GET_COMMUNITY_USERS_COUNTRIES,
  GET_ALL_COMPANIES,
} from '../../../api';
import styles from '../CommunityPage.module.scss';
import { mapUserInfoWindowMaker } from '../../../utils/googleMap';
import ListMode from './ListMode';
import CompanyListMode from './CompanyListMode';
import CompanyGridMode from './CompanyGridMode';
import GridMode from './GridMode';
import Filter from './Filter';
import { Map } from '../../../modules/tt-map';

const ProfCommunityPage = ({ activeRole, loading, setLoading }) => {
  const mapApiKey = process.env.REACT_APP_GOOGLE_MAP_KEY;
  const [activeMode, setActiveMode] = useState(
    localStorage.getItem('mode') ? localStorage.getItem('mode') : 'list'
  );
  const [activeCountry, setActiveCountry] = useState('all');
  const [users, setUsers] = useState(undefined);
  const [companies, setCompanies] = useState(undefined);
  const [searchValue, setSearchValue] = useState('');
  const [mapData, setMapData] = useState(undefined);
  const [page, setPage] = useState(1);
  const [getMore, setGetMore] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [scrollLoading, setScrollLoading] = useState(false);
  const [scrollLoadingCompanies, setScrollLoadingCompanies] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [usersLocations, setUsersLocations] = useState(undefined);
  const [usersCountries, setUsersCountries] = useState([]);
  const [companiesMapList, setCompaniesMapList] = useState([]);
  const [companiesMap, setCompaniesMap] = useState([]);
  const [firstRender, setFirstRender] = useState(false);
  const [filterCreatedAt, setFilterCreatedAt] = useState(false);
  const [filters, setFilters] = useState({
    role: activeRole,
    limit: 15,
    order_dir: 'desc',
    page: 1,
    q: '',
  });
  const [mapFilters, setMapFilters] = useState({
    role: activeRole,
  });
  const [communityUsers] = useLazyQuery(GET_COMMUNITY_USERS);
  const [communityUsersMap] = useLazyQuery(GET_COMMUNITY_USERS_MAP);
  const [communityUsersCountries] = useLazyQuery(GET_COMMUNITY_USERS_COUNTRIES);
  const [getAllCompanies] = useLazyQuery(GET_ALL_COMPANIES);

  useEffect(() => {
    if (localStorage.getItem('mode')) {
      setActiveMode(localStorage.getItem('mode'));
    } else {
      setActiveMode('list');
    }
  }, []);

  useEffect(() => {
    if (firstRender) {
      const delayDebounceFn = setTimeout(() => {
        setFilters({ ...filters, q: searchValue, page: 1 });
        setPage(1);
      }, 1500);

      return () => clearTimeout(delayDebounceFn);
    }
    setFirstRender(true);
  }, [searchValue]);

  useEffect(() => {
    setCompanies(undefined);
    setUsers(undefined);
    setFilters({ ...filters, page: 1, role: activeRole });
    setPage(1);
    setMapFilters({
      ...mapFilters,
      country: activeCountry === 'all' ? '' : activeCountry,
      role: activeRole,
    });
  }, [activeRole, activeCountry]);

  const getCommunityUsers = async () => {
    setLoading(true);
    setScrollLoading(true);
    if (!getMore) {
      setUsers(undefined);
    }
    let usersList;
    try {
      usersList = await communityUsers({
        variables: { input: filters },
      });
    } catch (e) {
      console.log(e);
    }
    if (usersList && usersList.data) {
      if (usersList.data.communityUsers !== null) {
        if (getMore) {
          setGetMore(false);
          setUsers((current) => [...current, ...(usersList.data?.communityUsers?.users ?? [])]);
        } else {
          setUsers(usersList.data.communityUsers.users);
        }
        setPageCount(Math.ceil((usersList.data?.communityUsers?.count ?? 0) / 15));
      } else {
        setScrollLoading(false);
      }
    }
    setLoading(false);
  };

  const getCommunityUsersMap = async () => {
    setScrollLoading(true);
    setLoading(true);
    const usersMapList = await communityUsersMap({
      variables: { input: mapFilters },
    });
    if (usersMapList.data) {
      if (usersMapList.data.communityUsersMap !== null) {
        const locations = usersMapList.data.communityUsersMap.users.map((user) => ({
          params: user.id,
          content: mapUserInfoWindowMaker(
            user.id,
            user.first_name,
            user.last_name,
            user.avatar,
            user.location.address,
            user.roles && user.roles[0]?.name,
            user.kyc
          ),
          position: {
            lng: parseFloat(user.location.latLng?.lng),
            lat: parseFloat(user.location.latLng?.lat),
          },
        }));
        setScrollLoading(false);
        setUsersLocations(locations);
      } else {
        setScrollLoading(false);
      }
    }
    setLoading(false);
  };

  const getCompanies = async () => {
    setLoading(true);
    if (!getMore) {
      setCompanies(undefined);
    }
    const data = {
      first: 15,
      input: {
        type: 'company',
        is_draft: false,
        ordersBy: filterCreatedAt
          ? [
              {
                column: 'created_at',
                direction: 'DESC',
              },
            ]
          : null,
      },
      page,
    };
    if (searchValue && searchValue.length >= 2) {
      data.input.partial_name = searchValue;
    }
    const companyListMap = await getAllCompanies({
      variables: { first: 250, page: 1 },
    });
    setCompaniesMapList(companyListMap.data.getCompaniesByWithPagination.data);
    const companyList = await getAllCompanies({
      variables: data,
    });
    if (companyList.data && companyList.data.getCompaniesByWithPagination.data) {
      if (getMore) {
        setGetMore(false);
        await setCompanies((current) => {
          if (current) {
            return [...current, ...companyList.data.getCompaniesByWithPagination.data];
          }
          return [...companyList.data.getCompaniesByWithPagination.data];
        });
        await setScrollLoading(false);
      } else {
        setCompanies(companyList.data.getCompaniesByWithPagination.data);
      }
      setPageCount(companyList.data.getCompaniesByWithPagination.paginatorInfo.lastPage);
      setScrollLoadingCompanies(false);
    }
    setLoading(false);
  };

  const GetCompaniesMap = async () => {
    const companyListMap = await getAllCompanies({
      variables: { first: 250, page: 1 },
    });
    const newArray =
      companyListMap.data.getCompaniesByWithPagination.data &&
      companyListMap.data.getCompaniesByWithPagination.data.filter(
        (item) => item.addressLocation !== null
      );
    const locations = newArray.map((user) => ({
      params: user.id,
      content: mapUserInfoWindowMaker(
        user.id,
        user.company_name,
        user.company_name,
        user.logos.length > 0 && user.logos[0].path,
        user.address1,
        filters.role,
        user.kyc
      ),
      position: {
        lng: parseFloat(user.addressLocation.lng),
        lat: parseFloat(user.addressLocation.lat),
      },
    }));
    setCompaniesMap(locations);
    setMapLoading(false);
  };

  useEffect(() => {
    if (users && usersLocations && activeMode === 'map') {
      const result =
        users &&
        usersLocations &&
        usersLocations
          .filter((item1) => users.some((item2) => item1.params === item2.id))
          .map((item) => item);
      setMapData(result);
      setMapLoading(false);
    }
  }, [users, usersLocations]);

  useEffect(() => {
    (async () => {
      if (filters.role && filters.role !== 'companies') {
        try {
          await getCommunityUsers();
        } catch {
          console.log('error');
        }
      } else if (filters.role === 'companies') {
        setScrollLoadingCompanies(true);
        try {
          await getCompanies();
        } catch {
          console.log('error');
        }
      }
    })();
  }, [filters, filterCreatedAt]);

  const getCommunityUsersCountries = async () => {
    const usersCountriesList = await communityUsersCountries();
    if (usersCountriesList.data && usersCountriesList.data.communityCountries) {
      setUsersCountries(usersCountriesList.data.communityCountries);
    }
  };

  useEffect(() => {
    if (activeMode === 'map') {
      getCommunityUsersCountries();
    }
  }, [activeMode, activeRole]);

  useEffect(() => {
    if (activeMode === 'map') {
      if (activeRole === 'companies') {
        GetCompaniesMap();
      } else {
        getCommunityUsersMap();
      }
      setMapLoading(true);
    } else {
      setUsersLocations(undefined);
    }
  }, [activeMode, mapFilters, activeRole]);

  const fetchMoreUsers = async () => {
    if (
      page < pageCount &&
      !loading &&
      ((users && users.length) || (companies && companies.length))
    ) {
      const pageNumber = page + 1;
      setScrollLoadingCompanies(true);
      setPage(pageNumber);
      setGetMore(true);
      setFilters((current) => ({ ...current, page: pageNumber }));
    }
  };

  const onScrollUsers = async (e) => {
    const { scrollHeight, scrollTop, clientHeight } = e.target;
    const maxScroll = scrollHeight - scrollTop - 1;
    if (maxScroll <= clientHeight && !getMore && (users || companies)) {
      await fetchMoreUsers();
    }
  };

  return (
    <div className={styles.communityContent}>
      <Filter
        activeRole={activeRole}
        setActiveCountry={(value) => setActiveCountry(value)}
        countries={usersCountries}
        setPage={(value) => setPage(value)}
        setFilters={(value) => setFilters(value)}
        activeMode={activeMode}
        changeSearchValue={(value) => setSearchValue(value)}
        searchValue={searchValue}
        changeActiveMode={(mode) => setActiveMode(mode)}
        setFilterCreatedAt={setFilterCreatedAt}
        filterCreatedAt={filterCreatedAt}
      />
      {activeMode === 'map' &&
        !loading &&
        (mapData || companiesMap ? (
          <Box
            sx={{
              height: 'calc(100vh - 325px)',
              borderRadius: 4,
              display: 'flex',
              justifyContent: 'center',
              my: 2,
              p: 2,
              backgroundColor: '#FFFFFF',
            }}
          >
            <Map
              showSearch={false}
              isList
              activeRole={activeRole}
              coordinatesList={activeRole === 'companies' ? companiesMap : mapData}
              apiKey={mapApiKey}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <p>List is empty</p>
          </Box>
        ))}
      {activeMode !== 'map' &&
        !loading &&
        users &&
        users.length === 0 &&
        activeRole !== 'companies' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <p>No users</p>
          </Box>
        )}
      {activeMode !== 'map' &&
        !loading &&
        companies &&
        companies.length === 0 &&
        activeRole === 'companies' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <p>No Companies</p>
          </Box>
        )}
      {activeRole !== 'companies' && activeMode !== 'map' && !users && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress
            sx={{
              width: '40px !important',
              height: '40px !important',
            }}
          />
        </Box>
      )}
      <div>
        {activeMode === 'list' && activeRole !== 'companies' && (
          <ListMode loading={scrollLoading} onScroll={onScrollUsers} users={users} />
        )}
        {activeMode === 'list' && activeRole === 'companies' && (
          <CompanyListMode loading={scrollLoading} onScroll={onScrollUsers} companies={companies} />
        )}
        {activeMode === 'grid' && activeRole !== 'companies' && (
          <GridMode loading={scrollLoading} onScroll={onScrollUsers} users={users} />
        )}
        {activeMode === 'grid' && activeRole === 'companies' && (
          <CompanyGridMode
            loading={scrollLoadingCompanies}
            onScroll={onScrollUsers}
            companies={companies}
          />
        )}
      </div>
    </div>
  );
};
export default ProfCommunityPage;
